import type { Client, RequestContext } from "../client";
import type { FeatureToggleResource, ProjectResource, ToggleStrategy, TenantTargetingStrategy, ResourceWithId } from "../resources";
export interface ListFeatureTogglesResponse {
    FeatureToggles: FeatureToggleResource[];
}
export interface ModifyFeatureToggleCommand {
    Id: string;
    ProjectId: string;
    SpaceId: string;
    Name: string;
    Slug: string;
    Environments: FeatureToggleEnvironment[];
}
export type CreateFeatureToggleCommand = Omit<ModifyFeatureToggleCommand, "Id" | "Slug" | "SpaceId">;
interface ModifyFeatureToggleResponse extends ResourceWithId {
    ProjectId: string;
    SpaceId: string;
    Name: string;
    Slug: string;
    Environments: FeatureToggleEnvironment[];
}
interface Segment {
    Key: string;
    Value: string;
}
interface FeatureToggleEnvironment {
    DeploymentEnvironmentId: string;
    IsEnabled: boolean;
    ToggleStrategy: ToggleStrategy;
    TenantTargetingStrategy: TenantTargetingStrategy;
    TenantIds: string[];
    RolloutPercentage: number | null;
    ExcludedTenantIds: string[];
    Segments: Segment[];
}
export interface GenerateClientIdentifierResponse {
    ClientIdentifier: string;
}
export interface TenantProjectEnvironmentDetail {
    TenantId: string;
    Name: string;
    ProjectId: string;
    EnvironmentId: string;
}
interface GetTenantsConnectedToEnvironmentForProjectBffResponse {
    Tenants: TenantProjectEnvironmentDetail[];
}
export class FeatureToggleRepository {
    protected client: Client;
    protected apiBaseRoute: string = `~/api/spaces/{spaceId}/projects/{projectId}/featuretoggles`;
    protected bffBaseRoute: string = `~/bff/spaces/{spaceId}/projects/{projectId}/featuretoggles`;
    constructor(client: Client, private readonly requestContext: RequestContext) {
        this.client = client;
    }
    async list(project: ProjectResource) {
        return this.client.get<ListFeatureTogglesResponse>(this.requestContext, this.apiBaseRoute, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
    }
    async get(project: ProjectResource, featureToggleSlug: string) {
        return this.client.get<FeatureToggleResource>(this.requestContext, `${this.apiBaseRoute}/{featureToggleSlug}`, {
            spaceId: project.SpaceId,
            projectId: project.Id,
            featureToggleSlug,
        });
    }
    async create(project: ProjectResource, command: CreateFeatureToggleCommand): Promise<FeatureToggleResource> {
        const response = await this.client.create<CreateFeatureToggleCommand, ModifyFeatureToggleResponse>(this.requestContext, this.apiBaseRoute, command, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
        return await this.get(project, response.Slug);
    }
    async modify(project: ProjectResource, resource: ModifyFeatureToggleCommand) {
        const response = await this.client.update<ModifyFeatureToggleCommand>(this.requestContext, this.apiBaseRoute, resource, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
        return await this.get(project, response.Slug);
    }
    async delete(project: ProjectResource, featureToggleId: string) {
        await this.client.del(this.requestContext, `${this.apiBaseRoute}/{featureToggleId}`, null, {
            spaceId: project.SpaceId,
            projectId: project.Id,
            featureToggleId,
        });
    }
    async generateClientIdentifier(project: ProjectResource, environmentId: string, tenantId: string | undefined) {
        const response = await this.client.post<GenerateClientIdentifierResponse>(this.requestContext, `${this.apiBaseRoute}/generate-client-identifier`, {
            spaceId: project.SpaceId,
            projectId: project.Id,
            environmentId: environmentId,
            tenantId: tenantId,
        }, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
        return response;
    }
    async rotateClientIdentifierSigningKey(project: ProjectResource, completeRotation: boolean) {
        await this.client.post(this.requestContext, `${this.apiBaseRoute}/rotate-client-identifier-signing-key`, {
            spaceId: project.SpaceId,
            projectId: project.Id,
            CompleteRotation: completeRotation,
        }, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
    }
    async getTenantsConnectedToProjectAndEnvironment(project: ProjectResource, environmentId: string) {
        const result = await this.client.get<GetTenantsConnectedToEnvironmentForProjectBffResponse>(this.requestContext, `${this.bffBaseRoute}/environment/{environmentId}/tenants`, {
            spaceId: project.SpaceId,
            projectId: project.Id,
            environmentId: environmentId,
        });
        return result.Tenants;
    }
    async getClientIdentifierRotationState(project: ProjectResource) {
        return await this.client.get<{
            RotationState: "Rotating" | "NotRotating";
        }>(this.requestContext, `${this.bffBaseRoute}/clientidentifier/rotation-state`, {
            spaceId: project.SpaceId,
            projectId: project.Id,
        });
    }
}
